import { Box } from "@mui/material";
import { DateInput, required, SelectInput, SimpleForm, TextInput, useRecordContext, useTranslate } from "react-admin";
import { FieldValues } from "react-hook-form";

import { Salutation, UpsertPatientInput } from "../generated/graphql";
import { WithAvatar } from "../hooks/useImageUploadHandler";
import { space } from "../theme/designTokens";
import { AvatarInput } from "./AvatarInput";
import { LocationHierarchyFields } from "./LocationHierarchyFields";

type PatientFormInput = Pick<
  UpsertPatientInput,
  "firstName" | "lastName" | "salutation" | "birthDate" | "patientNumber" | "locationId" | "organizationId" | "gender"
>;

export type PatientFormData = FieldValues &
  Omit<PatientFormInput, "locationId"> & {
    locationId?: string | null;
  } & WithAvatar;

type PatientFormProps = {
  toolbar: React.ReactElement;
};

export const PatientForm = ({ toolbar }: PatientFormProps) => {
  const record = useRecordContext();
  const translate = useTranslate();

  const salutationOptions = [
    { id: Salutation.Mister, name: translate("salutation.mister") },
    { id: Salutation.Missus, name: translate("salutation.missus") },
    { id: Salutation.Others, name: translate("salutation.others") },
  ];

  const genderOptions = [
    { id: "MALE", name: translate("gender.MALE") },
    { id: "FEMALE", name: translate("gender.FEMALE") },
    { id: "OTHER", name: translate("gender.OTHER") },
  ];

  return (
    <SimpleForm toolbar={toolbar}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <AvatarInput name="avatar" source={record?.avatarUrl} />
        <SelectInput
          source="salutation"
          label={translate("salutationLabel")}
          choices={salutationOptions}
          fullWidth
          validate={required()}
        />
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: `${space.xs}px`, width: "100%" }}>
          <TextInput source="firstName" label={translate("firstName")} required fullWidth />
          <TextInput source="lastName" label={translate("lastName")} required fullWidth />
        </Box>
        <DateInput source="birthDate" label={translate("birthDate")} validate={required()} size="medium" fullWidth />
        <SelectInput
          source="gender"
          label={translate("genderLabel")}
          choices={genderOptions}
          fullWidth
          validate={required()}
        />

        <TextInput source="patientNumber" label={translate("patientId")} required fullWidth />
        <LocationHierarchyFields />
      </Box>
    </SimpleForm>
  );
};
